import dayjs from 'dayjs';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  TableCell,
  TableFooter,
  TablePagination,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import PeopleIcon from '@mui/icons-material/People';
import DownloadIcon from '@mui/icons-material/Download';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import API from '../../../api';
import { HttpErrorHandler } from '../../../utils/ErrorHandler';
import { CustomTextField } from '../../../utils/StyledInputs';
import { EntitiesDialog } from '../../../utils/EntitiesDialog';
import { CustomIconButton, DefaultContainedButton, DefaultOutlinedButton } from '../../../utils/Buttons';
import { TablePaginationActions, theme } from '../../../utils/TableStyles';
import { EMPTY_CONDITION, PREVIEW_COLUMNS } from './filterConfig';
import ConditionRow from './components/ConditionRow';
import { usePopup } from '../../../context/PopupContext';
import { CommonLabel } from '../../../utils/Common';

const valuelessOperators = ['is_null', 'is_not_null'];

const isValidDate = (val) => dayjs(val, 'YYYY-MM-DD', true).isValid();

export default function FilterDetails() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { showPopup, setPopUpText } = usePopup();

  const mode = state?.mode || 'create';

  // -------------------- Filter state --------------------
  const [filterName, setFilterName] = useState('');
  const [filterEntity, setFilterEntity] = useState(null);
  const [filterStatus, setFilterStatus] = useState('draft');
  const [filterArchived, setFilterArchived] = useState(false);
  const [openEntitiesDialog, setOpenEntitiesDialog] = useState(false);
  const [conditions, setConditions] = useState([{ ...EMPTY_CONDITION, or_group: 0, and_group: 0 }]);
  const [isSaved, setIsSaved] = useState(mode === 'edit');
  const [pageLoading, setPageLoading] = useState(mode === 'edit');
  const [currentFilterID, setCurrentFilterID] = useState(state?.filterID || null);

  const isSupervisor = (() => {
    try {
      const data = JSON.parse(localStorage.getItem('userData'));
      return !!data?.user_supervisor;
    } catch {
      return false;
    }
  })();

  // -------------------- Filterable fields from API --------------------
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);

  useEffect(() => {
    API.get('/filterable_fields')
      .then((response) => {
        setFields(response.data);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      })
      .finally(() => setFieldsLoading(false));
  }, []);

  // -------------------- Load existing filter (edit mode) --------------------
  useEffect(() => {
    if (mode !== 'edit' || !state?.filterID || fieldsLoading) return;

    API.get(`/filters/${state.filterID}`)
      .then((response) => {
        const filter = response.data;
        setFilterName(filter.name);
        setFilterEntity(filter.entity || null);
        setFilterStatus(filter.status || 'draft');
        setFilterArchived(!!filter.is_archived);
        setConditions(
          filter.conditions.map((c) => {
            const fieldName = c.filterable_field?.field || fields.find((f) => f.id === c.field_id)?.field || '';
            return {
              field: fieldName,
              operator: c.operator || '',
              value: c.operator === 'between' ? c.value?.split(',')[0] || '' : c.value || '',
              value2: c.operator === 'between' ? c.value?.split(',')[1] || '' : '',
              or_group: c.or_group ?? 0,
              and_group: c.and_group ?? 0,
            };
          })
        );
      })
      .catch((error) => {
        HttpErrorHandler(error);
      })
      .finally(() => setPageLoading(false));
  }, [mode, state?.filterID, fieldsLoading]);

  // -------------------- Preview state --------------------
  const [previewRows, setPreviewRows] = useState([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const [fullPreview, setFullPreview] = useState(false);

  // Title based on mode
  const pageTitle = mode === 'create' ? 'Nuovo Filtro' : mode === 'duplicate' ? 'Duplica Filtro' : 'Modifica Filtro';

  // Derive preview columns dynamically from first row, using PREVIEW_COLUMNS labels
  const previewLabelMap = Object.fromEntries(PREVIEW_COLUMNS.map((c) => [c.field, c.label]));
  const previewColumns =
    previewRows.length > 0
      ? Object.keys(previewRows[0])
          .filter((k) => k !== 'id')
          .map((k) => ({ field: k, label: previewLabelMap[k] || k }))
      : [];

  const renderCellValue = (val) => {
    if (val == null) return '';
    if (Array.isArray(val)) {
      return val
        .map((item) =>
          item != null && typeof item === 'object' ? item.value || item.name || item.label || item.title || '' : item
        )
        .filter((v) => v !== '' && v != null)
        .join(', ');
    }
    if (typeof val === 'object') return val.value || val.name || val.label || val.title || '';
    return val;
  };

  // -------------------- Condition handlers --------------------
  const handleConditionChange = (index, updated) => {
    setConditions((prev) => prev.map((c, i) => (i === index ? updated : c)));
  };

  const handleAddCondition = (andGroup) => {
    const maxOr = conditions
      .filter((c) => c.and_group === andGroup)
      .reduce((max, c) => Math.max(max, c.or_group), -1);
    setConditions((prev) => [...prev, { ...EMPTY_CONDITION, or_group: maxOr + 1, and_group: andGroup }]);
  };

  const handleAddOrCondition = (andGroup, orGroup) => {
    setConditions((prev) => [...prev, { ...EMPTY_CONDITION, or_group: orGroup, and_group: andGroup }]);
  };

  const handleAddAndBlock = () => {
    const maxAnd = conditions.reduce((max, c) => Math.max(max, c.and_group), -1);
    setConditions((prev) => [...prev, { ...EMPTY_CONDITION, or_group: 0, and_group: maxAnd + 1 }]);
  };

  const handleRemoveCondition = (index) => {
    setConditions((prev) => prev.filter((_, i) => i !== index));
  };

  // -------------------- Preview (after save) --------------------
  const isConditionValid = useCallback(
    (c) => {
      if (!c.field || !c.operator) return false;
      if (valuelessOperators.includes(c.operator)) return true;

      const fieldObj = fields.find((f) => f.field === c.field);
      const isDate = fieldObj?.field_type === 'date';

      if (c.operator === 'between') {
        if (!c.value || !c.value2) return false;
        return isDate ? isValidDate(c.value) && isValidDate(c.value2) : true;
      }

      if (!c.value) return false;
      return isDate ? isValidDate(c.value) : true;
    },
    [fields]
  );

  const hasValidConditions = conditions.some(isConditionValid);

  // Normalize and_group / or_group to contiguous indices (0, 1, 2...) per block for payload
  const normalizeConditions = useCallback(
    (condList) => {
      const andMap = new Map();
      const orMaps = new Map();
      let nextAnd = 0;
      return condList.filter(isConditionValid).map((c) => {
        if (!andMap.has(c.and_group)) {
          andMap.set(c.and_group, nextAnd);
          orMaps.set(c.and_group, new Map());
          nextAnd += 1;
        }
        const newAnd = andMap.get(c.and_group);
        const orMap = orMaps.get(c.and_group);
        if (!orMap.has(c.or_group)) {
          orMap.set(c.or_group, orMap.size);
        }
        const fieldObj = fields.find((f) => f.field === c.field);
        return {
          field_id: fieldObj?.id,
          operator: c.operator,
          value: c.operator === 'between' ? `${c.value},${c.value2}` : c.value || null,
          or_group: orMap.get(c.or_group),
          and_group: newAnd,
        };
      });
    },
    [fields, isConditionValid]
  );

  const buildConditionsPayload = useCallback(() => normalizeConditions(conditions), [conditions, normalizeConditions]);

  const fetchPreview = useCallback(
    ({ page = 0, paginated = false } = {}) => {
      if (!hasValidConditions || !filterEntity?.id) {
        setPreviewRows([]);
        setPreviewTotal(0);
        return;
      }

      setPreviewLoading(true);

      const payload = {
        entity_id: filterEntity.id,
        conditions: buildConditionsPayload(),
        ...(currentFilterID && { filter_id: currentFilterID }),
      };

      const endpoint = paginated ? '/filters/preview?complete' : '/filters/preview';

      if (paginated) {
        payload.page = page + 1;
        payload.per_page = 10;
      }

      API.post(endpoint, payload)
        .then((response) => {
          if (paginated) {
            setPreviewRows(response.data.data || []);
            setPreviewTotal(response.data.total || 0);
          } else {
            setPreviewRows(response.data.preview || []);
            setPreviewTotal(response.data.total_count || 0);
          }
        })
        .catch(() => {
          setPreviewRows([]);
          setPreviewTotal(0);
        })
        .finally(() => setPreviewLoading(false));
    },
    [hasValidConditions, filterEntity, buildConditionsPayload, currentFilterID]
  );

  // Fetch preview on edit mode load (only when page finishes loading)
  useEffect(() => {
    if (mode === 'edit' && isSaved && hasValidConditions && !pageLoading) {
      fetchPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLoading]);

  // -------------------- Export --------------------
  const [exporting, setExporting] = useState(false);

  const handleExport = (withContacts) => {
    setExporting(true);

    const payload = {
      entity_id: filterEntity?.id,
      conditions: buildConditionsPayload(),
      with_contacts: withContacts,
      filter_name: filterName || undefined,
      filter_id: currentFilterID || undefined,
    };

    API.post('/filters/export', payload)
      .then((response) => {
        showPopup(true, response.data.message || 'Esportazione in corso. Riceverai il file via email.');
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      })
      .finally(() => setExporting(false));
  };

  // -------------------- Save --------------------
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);

    const payload = {
      name: filterName,
      entity_id: filterEntity?.id,
      status: filterStatus,
      is_archived: filterArchived,
      conditions: normalizeConditions(conditions),
    };

    const isUpdate = !!currentFilterID;
    const request = isUpdate ? API.put(`/filters/${currentFilterID}`, payload) : API.post('/filters', payload);

    request
      .then((response) => {
        if (!isUpdate && response.data?.data?.id) {
          setCurrentFilterID(response.data.data.id);
        }
        setIsSaved(true);
        setFullPreview(false);
        setPreviewPage(0);
        fetchPreview();
        showPopup(true, `Filtro ${isUpdate ? 'aggiornato' : 'creato'} con successo`);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      })
      .finally(() => setSaving(false));
  };

  const canSave = filterName.trim() && filterEntity && hasValidConditions && !saving;

  // -------------------- Grouped conditions for rendering --------------------
  // Two-level: and_blocks (top-level OR) > or_groups (AND within block) > conditions (OR within group)
  const indexedConditions = conditions.map((c, i) => ({ ...c, _index: i }));

  const andOrder = [];
  const seenAndGroups = new Set();
  indexedConditions.forEach((c) => {
    if (!seenAndGroups.has(c.and_group)) {
      seenAndGroups.add(c.and_group);
      andOrder.push(c.and_group);
    }
  });

  const andBlocks = andOrder.map((andGroup) => {
    const blockConditions = indexedConditions.filter((c) => c.and_group === andGroup);
    const orOrder = [];
    const seenOrGroups = new Set();
    blockConditions.forEach((c) => {
      if (!seenOrGroups.has(c.or_group)) {
        seenOrGroups.add(c.or_group);
        orOrder.push(c.or_group);
      }
    });
    return {
      andGroup,
      orGroups: orOrder.map((orGroup) => ({
        orGroup,
        conditions: blockConditions.filter((c) => c.or_group === orGroup),
      })),
    };
  });

  if (pageLoading) {
    return (
      <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <DefaultOutlinedButton onClick={() => navigate('/admin/filters')} startIcon={<ArrowBackIcon />}>
            Indietro
          </DefaultOutlinedButton>
          <Typography variant="h4">{pageTitle}</Typography>
        </Stack>

        {/* Metadata card - full width */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2.5}>
              <CustomTextField label="Nome filtro" value={filterName} onChange={(e) => setFilterName(e.target.value)} />

              <CustomTextField
                label="Entità"
                value={filterEntity ? filterEntity.name : ''}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                      {filterEntity ? (
                        <Tooltip title="Elimina" placement="top" arrow>
                          <DeleteIcon onClick={() => setFilterEntity(null)} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Seleziona" placement="top" arrow>
                          <EditIcon onClick={() => setOpenEntitiesDialog(true)} />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              <EntitiesDialog
                openDialog={openEntitiesDialog}
                closeDialog={() => setOpenEntitiesDialog(false)}
                outputEntity={(entity) => setFilterEntity(entity)}
                context={'users'}
              />

              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#637381' }}>
                  Stato:
                </Typography>
                <RadioGroup row value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <FormControlLabel value="draft" control={<Radio size="small" />} label="Bozza" />
                  <FormControlLabel value="visible" control={<Radio size="small" />} label="Pubblicato" />
                </RadioGroup>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#637381' }}>
                  Archiviazione:
                </Typography>
                <RadioGroup
                  row
                  value={filterArchived ? 'archived' : 'active'}
                  onChange={(e) => setFilterArchived(e.target.value === 'archived')}
                >
                  <FormControlLabel value="active" control={<Radio size="small" />} label="Attivo" />
                  <FormControlLabel value="archived" control={<Radio size="small" />} label="Archiviato" />
                </RadioGroup>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Conditions card - full width */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Condizioni
            </Typography>

            {fieldsLoading ? (
              <Stack alignItems="center" py={4}>
                <CircularProgress size={28} />
              </Stack>
            ) : (
              <>
                <Stack spacing={1.5}>
                  {andBlocks.map((andBlock, andIdx) => {
                    const hasMultipleAndBlocks = andBlocks.length > 1;

                    return (
                      <div key={andBlock.andGroup}>
                        {/* Top-level OR divider between and_blocks */}
                        {andIdx > 0 && (
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 3 }}>
                            <Divider sx={{ flex: 1, borderColor: '#2e7d32', opacity: 0.4 }} />
                            <Chip
                              label="OPPURE"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                backgroundColor: '#2e7d32',
                                color: '#fff',
                                fontSize: '0.75rem',
                                letterSpacing: '0.05em',
                              }}
                            />
                            <Divider sx={{ flex: 1, borderColor: '#2e7d32', opacity: 0.4 }} />
                          </Stack>
                        )}

                        {/* AND block container - bordered only when multiple and_blocks */}
                        <Box
                          sx={
                            hasMultipleAndBlocks
                              ? {
                                  border: '1px solid #2e7d32',
                                  borderRadius: 2,
                                  px: 2,
                                  pt: 2.5,
                                  pb: 2,
                                  position: 'relative',
                                }
                              : {}
                          }
                        >
                          {hasMultipleAndBlocks && (
                            <Typography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                top: -10,
                                left: 12,
                                backgroundColor: '#fff',
                                px: 1,
                                color: '#2e7d32',
                                fontWeight: 700,
                                fontSize: '0.7rem',
                              }}
                            >
                              {`Blocco ${andIdx + 1}`}
                            </Typography>
                          )}

                          <Stack spacing={1.5}>
                            {andBlock.orGroups.map(({ orGroup, conditions: groupConditions }, groupIdx) => {
                              const isOrGroup = groupConditions.length > 1;

                              return (
                                <div key={orGroup}>
                                  {/* AND divider between or_groups within the and_block */}
                                  {groupIdx > 0 && (
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2 }}>
                                      <Divider sx={{ flex: 1, borderColor: '#1565c0', opacity: 0.3 }} />
                                      <Chip
                                        label="E"
                                        size="small"
                                        sx={{
                                          fontWeight: 700,
                                          backgroundColor: '#1565c0',
                                          color: '#fff',
                                          fontSize: '0.7rem',
                                        }}
                                      />
                                      <Divider sx={{ flex: 1, borderColor: '#1565c0', opacity: 0.3 }} />
                                    </Stack>
                                  )}

                                  {/* Condition(s) - boxed only when OR group */}
                                  <Stack
                                    spacing={1}
                                    sx={
                                      isOrGroup
                                        ? {
                                            border: '1px solid #e65100',
                                            borderRadius: 2,
                                            pl: 2,
                                            pr: 1.5,
                                            py: 1.5,
                                            position: 'relative',
                                          }
                                        : {}
                                    }
                                  >
                                    {isOrGroup && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          position: 'absolute',
                                          top: -10,
                                          left: 12,
                                          backgroundColor: '#fff',
                                          px: 1,
                                          color: '#e65100',
                                          fontWeight: 700,
                                          fontSize: '0.7rem',
                                        }}
                                      >
                                        Almeno una di queste
                                      </Typography>
                                    )}

                                    {groupConditions.map((condition, condIdx) => (
                                      <div key={condition._index}>
                                        {/* OR divider within or_group */}
                                        {condIdx > 0 && (
                                          <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 0.5 }}>
                                            <Divider sx={{ flex: 1, borderColor: '#e65100', opacity: 0.3 }} />
                                            <Chip
                                              label="Oppure"
                                              size="small"
                                              sx={{
                                                fontWeight: 700,
                                                backgroundColor: '#e65100',
                                                color: '#fff',
                                                fontSize: '0.7rem',
                                              }}
                                            />
                                            <Divider sx={{ flex: 1, borderColor: '#e65100', opacity: 0.3 }} />
                                          </Stack>
                                        )}
                                        <ConditionRow
                                          condition={condition}
                                          index={condition._index}
                                          fields={fields}
                                          onChange={handleConditionChange}
                                          onRemove={handleRemoveCondition}
                                          isOnly={conditions.length === 1}
                                        />
                                      </div>
                                    ))}

                                    {/* Add OR alternative within this or_group */}
                                    <Typography
                                      variant="body2"
                                      onClick={() => handleAddOrCondition(andBlock.andGroup, orGroup)}
                                      sx={{
                                        color: '#e65100',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        mt: 0.5,
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                    >
                                      + Aggiungi alternativa
                                    </Typography>
                                  </Stack>
                                </div>
                              );
                            })}
                          </Stack>

                          {/* Add criterion within this and_block */}
                          <DefaultOutlinedButton
                            onClick={() => handleAddCondition(andBlock.andGroup)}
                            startIcon={<AddIcon />}
                            customStyles={{
                              mt: 2,
                              width: '100%',
                              borderColor: '#1565c0',
                              color: '#1565c0',
                              '&:hover': { borderColor: '#0d47a1', backgroundColor: '#e3f2fd' },
                            }}
                          >
                            Aggiungi criterio
                          </DefaultOutlinedButton>
                        </Box>
                      </div>
                    );
                  })}
                </Stack>

                {/* Add a new top-level and_block (alternative in OR) */}
                <DefaultOutlinedButton
                  onClick={handleAddAndBlock}
                  startIcon={<AddIcon />}
                  customStyles={{
                    mt: 3,
                    width: '100%',
                    borderColor: '#2e7d32',
                    color: '#2e7d32',
                    '&:hover': { borderColor: '#1b5e20', backgroundColor: '#e8f5e9' },
                  }}
                >
                  Aggiungi blocco alternativo
                </DefaultOutlinedButton>
              </>
            )}
          </CardContent>
        </Card>

        {/* Action buttons */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <DefaultContainedButton onClick={handleSave} disabled={!canSave} startIcon={<SaveIcon />}>
            {saving ? 'Salvataggio...' : 'Salva filtro'}
          </DefaultContainedButton>
          <DefaultOutlinedButton onClick={() => navigate('/admin/filters')}>Annulla</DefaultOutlinedButton>
        </Stack>

        {/* Preview - shown only after save */}
        {isSaved && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              {/* Preview header */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                spacing={2}
                mb={2}
              >
                <Typography variant="h6">Anteprima risultati</Typography>
                {hasValidConditions && !previewLoading && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={1.5}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                  >
                    <Chip
                      icon={<PeopleIcon />}
                      label={`${previewTotal.toLocaleString('it-IT')} risultati totali`}
                      color="primary"
                      sx={{ fontWeight: 600, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    />
                    {previewTotal > 0 && (
                      <>
                        <DefaultOutlinedButton
                          startIcon={<DownloadIcon />}
                          onClick={() => handleExport(false)}
                          disabled={exporting}
                        >
                          {exporting ? 'Esportazione...' : 'Esporta'}
                        </DefaultOutlinedButton>
                        {isSupervisor && (
                          <DefaultOutlinedButton
                            startIcon={<DownloadIcon />}
                            onClick={() => handleExport(true)}
                            disabled={exporting}
                          >
                            {exporting ? 'Esportazione...' : 'Esporta con contatti'}
                          </DefaultOutlinedButton>
                        )}
                      </>
                    )}
                  </Stack>
                )}
              </Stack>

              {/* Preview content */}
              {previewLoading ? (
                <Stack alignItems="center" py={6}>
                  <CircularProgress size={36} sx={{ color: '#212b36' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Caricamento anteprima...
                  </Typography>
                </Stack>
              ) : !hasValidConditions ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Aggiungi almeno una condizione valida per visualizzare l'anteprima dei risultati.
                </Alert>
              ) : previewTotal === 0 ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Nessun risultato disponibile
                </Alert>
              ) : (
                <>
                  <TableContainer sx={{ maxHeight: 520 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {previewColumns.map((col) => (
                            <TableCell key={col.field} sx={{ fontWeight: 600, backgroundColor: '#f4f6f8' }}>
                              {col.label}
                            </TableCell>
                          ))}
                          <TableCell sx={{ pr: 3, fontWeight: 600, backgroundColor: '#f4f6f8' }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewRows.map((row, idx) => (
                          <TableRow key={idx} hover>
                            {previewColumns.map((col) => (
                              <TableCell key={col.field}>
                                {col.field === 'birth_date' || col.field === 'created_at' ? (
                                  row[col.field] ? (
                                    dayjs(row[col.field]).format('DD/MM/YYYY')
                                  ) : (
                                    ' '
                                  )
                                ) : col.field === 'verified' ||
                                  col.field === 'user_online' ||
                                  col.field === 'user_status' ||
                                  col.field === 'allow_news' ||
                                  // Email/Telefono: colonne PII mostrate solo quando filtrate → mai il
                                  // valore, solo la presenza (check/x), come le altre colonne booleane.
                                  col.field === 'email' ||
                                  col.field === 'phone' ? (
                                  row[col.field] ? (
                                    <CheckIcon style={{ color: 'green' }} />
                                  ) : (
                                    <CloseIcon style={{ color: '#d0342b' }} />
                                  )
                                ) : col.field === 'gender' ? (
                                  row[col.field] === 'male' ? (
                                    'Uomo'
                                  ) : row[col.field] === 'female' ? (
                                    'Donna'
                                  ) : (
                                    'Altro'
                                  )
                                ) : col.field === 'pipeline_stage' ? (
                                  <CommonLabel
                                    textColor="white"
                                    bgColor={
                                      row[col.field] === 'contact'
                                        ? '#1a73e8'
                                        : row[col.field] === 'member'
                                        ? 'green'
                                        : 'red'
                                    }
                                    text={
                                      row[col.field] === 'contact'
                                        ? 'Contatto'
                                        : row[col.field] === 'member'
                                        ? 'Membro'
                                        : 'Utente'
                                    }
                                  />
                                ) : (
                                  renderCellValue(row[col.field])
                                )}
                              </TableCell>
                            ))}
                            <TableCell>
                              <CustomIconButton
                                onClick={() => {
                                  navigate('/admin/users/user-details', { state: { userID: row?.id } });
                                }}
                              >
                                <KeyboardArrowRightIcon />
                              </CustomIconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {fullPreview ? (
                    <Table>
                      <TableFooter>
                        <TableRow>
                          <ThemeProvider theme={theme}>
                            <TablePagination
                              sx={{ borderBottom: 'none' }}
                              rowsPerPageOptions={[]}
                              count={previewTotal}
                              rowsPerPage={10}
                              page={previewPage}
                              onPageChange={(_, newPage) => {
                                setPreviewPage(newPage);
                                fetchPreview({ page: newPage, paginated: true });
                              }}
                              ActionsComponent={TablePaginationActions}
                            />
                          </ThemeProvider>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  ) : (
                    <Stack alignItems="center" sx={{ mt: 2 }}>
                      <DefaultContainedButton
                        startIcon={<PeopleIcon />}
                        onClick={() => {
                          setFullPreview(true);
                          setPreviewPage(0);
                          fetchPreview({ page: 0, paginated: true });
                        }}
                      >
                        Visualizza tutti i risultati
                      </DefaultContainedButton>
                    </Stack>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </Container>
    </>
  );
}
